import { zodResolver } from "@hookform/resolvers/zod"
import { getStockByStoreIds, localStorage } from "@jog/shared"
import { JoggStore } from "@jog/types"
import { useCallback, useState } from "react"
import { z } from "zod"

import { useForm } from "../ui"
import { getStoreLocator, useUserLocation } from "./useLocation"

const searchSchema = z.object({
    sku: z.string().min(1, { message: "Select your size" }),
    address: z.string().refine(
        (value) => {
            if (value.length < 3) return false
            if (/\d/.test(value)) {
                if (/^[1-9][0-9]{3}\s?[a-zA-Z]{2}$/.test(value)) return true
                else if (/^[1-9][0-9]{3}$/.test(value)) return true
                else return false
            }
            return true
        },
        { message: "Location not available. Allow location use in your browser settings." }
    ),
})
type SearchSchema = z.infer<typeof searchSchema>
export const useStocks = (initialSku?: string) => {
    const PAGE_SIZE = 6
    const { getLocation } = useUserLocation()
    const [stores, setStores] = useState<{ list: JoggStore[]; search: string }>({ list: [], search: "" })
    const [storeViews, setStoreViews] = useState<JoggStore[]>([])
    const [stocks, setStocks] = useState<Record<string, number>>({})
    const [pending, setPending] = useState(false)
    const form = useForm<SearchSchema>({
        resolver: zodResolver(searchSchema),
        reValidateMode: "onBlur",
        defaultValues: async () => {
            const lastSearchAddress = localStorage.getItem<{ address: string }>("last_search_address")
            return { sku: initialSku || "", address: lastSearchAddress?.address || "" }
        },
    })
    const getStock = useCallback(async (sku: string, storeViews: JoggStore[]) => {
        setPending(true)
        const stocks = await getStockByStoreIds(
            sku,
            storeViews.map((i) => i.storeNumber)
        )
        setStocks((pre) => ({ ...(pre || {}), ...stocks }))
        setStoreViews((pre) => [...pre, ...storeViews])
    }, [])
    const loadmore = useCallback(async () => {
        const sku = form.getValues("sku")
        const index = stores.list.findIndex((store) => store.storeNumber === storeViews?.at(-1)?.storeNumber)
        if (index > -1) {
            await getStock(sku, stores.list.slice(index + 1, index + PAGE_SIZE + 1))
                .catch((error) => {
                    form.setError("address", {
                        message: "Location not available. Allow location use in your browser settings.",
                    })
                    console.error(error)
                })
                .finally(() => {
                    setPending(false)
                })
        }
    }, [form, getStock, storeViews, stores.list])
    const onSubmit = form.handleSubmit(async (data) => {
        if (pending) return
        setPending(true)
        setStoreViews([])
        try {
            const jcStores =
                data.address === stores.search
                    ? stores.list
                    : await getStoreLocator(data.address).then((res) =>
                          res.filter((i) => i.storeType === "Jeans Centre")
                      )
            if (jcStores.length === 0) {
                form.setError("address", {
                    message: "Location not available. Allow location use in your browser settings.",
                })
                setStocks({})
                setStores({ list: [], search: "" })
                return
            }
            setStores({ list: jcStores, search: data.address })
            await getStock(data.sku, jcStores.slice(0, PAGE_SIZE))
            localStorage.setItem("last_search_address", { address: data.address })
        } catch (error) {
            form.setError("address", {
                message: "Location not available. Allow location use in your browser settings.",
            })
            console.error(error)
            setStocks({})
            setStoreViews([])
            setStores({ list: [], search: "" })
        } finally {
            setPending(false)
        }
    })

    const onMyLocation = useCallback(async () => {
        const city = await getLocation().catch((e) => {
            console.log("error", e)
            form.setError("address", {
                message: "Location not available. Allow location use in your browser settings.",
            })
        })
        if (!city) return
        form.setValue("address", city)
        const sku = form.getValues("sku")
        if (sku) onSubmit()
    }, [getLocation, form, onSubmit])
    return {
        pending,
        loadmore,
        stores: storeViews,
        hasMore: storeViews.length < stores.list.length,
        stocks,
        form,
        onSubmit,
        onMyLocation,
    }
}
